"use client";

import type { CSSProperties, ReactNode } from "react";

import type { ConceptSpec } from "../spec";
import {
	Approval,
	Arrow,
	Dim,
	Hatch,
	Leader,
	Plate,
	Sheet,
	SheetHeading,
	TitleBlock,
} from "./drawing-kit";

/** Print order for `m-print` rows (the kit staggers on `--i`). */
const printOrder = (i: number): CSSProperties =>
	({ "--i": i }) as CSSProperties;

/* ================================================================= */
/*  Shared geometry helpers                                            */
/* ================================================================= */

const DEG = Math.PI / 180;

/** Point on a circle (SVG y grows downward). */
function polar(
	cx: number,
	cy: number,
	r: number,
	deg: number,
): [number, number] {
	return [cx + r * Math.cos(deg * DEG), cy + r * Math.sin(deg * DEG)];
}

function fmt(n: number) {
	return Number(n.toFixed(2));
}

/** Tick ring: `count` minor ticks between r0 and r1, every `majorEvery`th tick longer. */
function TickRing({
	cx,
	cy,
	r0,
	r1,
	count,
	majorEvery,
	majorLen = 8,
}: {
	cx: number;
	cy: number;
	r0: number;
	r1: number;
	count: number;
	majorEvery: number;
	majorLen?: number;
}) {
	const step = 360 / count;
	return (
		<g>
			{Array.from({ length: count }, (_, i) => {
				const deg = fmt(i * step);
				const major = i % majorEvery === 0;
				return (
					<line
						key={`tick-${deg}`}
						className={major ? "a-tick major" : "a-tick"}
						x1={cx + r0}
						y1={cy}
						x2={cx + r1 + (major ? majorLen : 0)}
						y2={cy}
						transform={`rotate(${deg} ${cx} ${cy})`}
					/>
				);
			})}
		</g>
	);
}

/* ================================================================= */
/*  DRW-001 · The missing context                                      */
/* ================================================================= */

const SPHERE = { cx: 210, cy: 320, r: 105 };
const LATITUDES = [-60, -30, 0, 30, 60];
const MERIDIANS = [22, 45, 68];
const PLACEHOLDERS = [
	"what your accounts mean",
	"how work moves",
	"which rules your team trusts",
];
const TWIN_RING = { cx: 640, cy: 404, r: 60 };
const STATIONS = [-90, -30, 30, 90, 150, 210];

function Drw001() {
	const { cx, cy, r } = SPHERE;
	const t = TWIN_RING;
	const [s1x, s1y] = polar(t.cx, t.cy, t.r, -30);
	const [s2x, s2y] = polar(t.cx, t.cy, t.r, 30);
	const [s3x, s3y] = polar(t.cx, t.cy, t.r, 90);
	return (
		<Plate
			figure="Fig. 01"
			caption="AI has intelligence. It does not know your business."
		>
			<Sheet
				label="Elevation: a sealed generic model on the left, a dimension gap marked the missing context, and on the right a drawing sheet of your business whose upper half is blank and whose lower half is filled by the Twin."
				height={620}
			>
				<SheetHeading
					label="Elevation"
					sub="01 · The missing context · scale 1:1"
				/>

				{/* ===== Generic model — sealed sphere ===== */}
				<g>
					<text className="a-label" x={cx} y={172} textAnchor="middle">
						Generic model
					</text>
					<text className="a-sub" x={cx} y={187} textAnchor="middle">
						01 · sealed · no ports
					</text>
					<path
						className="a-line axis"
						d={`M${cx - 130} ${cy} H${cx + 130} M${cx} ${cy - 130} V${cy + 130}`}
					/>
					<circle className="a-fill" cx={cx} cy={cy} r={r} />
					<circle className="a-ring" cx={cx} cy={cy} r={r} />
					{LATITUDES.map((phi) => {
						const rx = fmt(r * Math.cos(phi * DEG));
						const ly = fmt(cy + r * Math.sin(phi * DEG));
						return (
							<ellipse
								key={`lat-${phi}`}
								className="a-ring dash"
								cx={cx}
								cy={ly}
								rx={rx}
								ry={fmt(rx * 0.22)}
							/>
						);
					})}
					{MERIDIANS.map((lam) => (
						<ellipse
							key={`mer-${lam}`}
							className="a-ring dash"
							cx={cx}
							cy={cy}
							rx={fmt(r * Math.sin(lam * DEG))}
							ry={r}
						/>
					))}
					<circle className="a-node hollow" cx={cx} cy={cy} r="3.4" />
					<Dim
						x1={cx - r}
						y1={cy}
						x2={cx + r}
						y2={cy}
						offset={140}
						label="Ø GENERIC"
					/>
					<text className="a-note" x={cx} y={500} textAnchor="middle">
						general knowledge · no business context
					</text>
				</g>

				{/* ===== The gap ===== */}
				<g>
					<Dim
						x1={cx + r}
						y1={cy}
						x2={490}
						y2={cy}
						offset={-150}
						label="THE MISSING CONTEXT"
						accent
					/>
					<path
						className="a-line hidden-line"
						d={`M${cx + r - 6} ${cy - 28} H${438} M${cx + r} ${cy} H${438} M${cx + r - 6} ${cy + 28} H${438}`}
					/>
					<path
						className="a-line"
						d="M434 288 L442 296 M442 288 L434 296 M434 316 L442 324 M442 316 L434 324 M434 344 L442 352 M442 344 L434 352"
					/>
					<text className="a-sub" x={392} y={198} textAnchor="middle">
						answers arrive unverifiable
					</text>
				</g>

				{/* ===== Your business — a drawing sheet ===== */}
				<g>
					<rect
						className="a-fill paper"
						x="490"
						y="110"
						width="390"
						height="380"
					/>
					<rect
						className="a-line strong"
						x="490"
						y="110"
						width="390"
						height="380"
					/>
					<rect
						className="a-line faint"
						x="498"
						y="118"
						width="374"
						height="364"
					/>
					<path
						className="a-crop"
						d="M484 116 H496 M496 104 V116 M874 116 H886 M874 104 V116 M484 484 H496 M496 484 V496 M874 484 H886 M874 484 V496"
					/>
					<text className="a-note ink" x="508" y="136">
						Sheet · your business
					</text>
					<text className="a-sub" x="862" y="136" textAnchor="end">
						upper half · not drawn
					</text>
					<path className="a-line axis" d="M498 300 H872" />

					{/* upper half — hidden-line placeholders */}
					{PLACEHOLDERS.map((line, i) => {
						const y = 152 + i * 44;
						return (
							<g key={line}>
								<rect
									className="a-line hidden-line"
									x="510"
									y={y}
									width="350"
									height="32"
								/>
								<text className="a-note" x="522" y={y + 20}>
									{line}
								</text>
								<text className="a-sub" x="848" y={y + 20} textAnchor="end">
									—
								</text>
							</g>
						);
					})}

					{/* lower half — the Twin, plan view */}
					<text className="a-sub" x="862" y="322" textAnchor="end">
						02 · plan · filled by the Twin
					</text>
					<path
						className="a-line axis"
						d={`M${t.cx - 80} ${t.cy} H${t.cx + 80} M${t.cx} ${t.cy - 70} V${t.cy + 70}`}
					/>
					<circle className="a-ring dash" cx={t.cx} cy={t.cy} r={t.r + 12} />
					<circle className="a-ring" cx={t.cx} cy={t.cy} r={t.r} />
					<circle className="a-ring faint" cx={t.cx} cy={t.cy} r={t.r - 26} />
					<TickRing
						cx={t.cx}
						cy={t.cy}
						r0={t.r}
						r1={t.r + 6}
						count={36}
						majorEvery={6}
						majorLen={6}
					/>
					{STATIONS.map((deg, i) => {
						const [x, y] = polar(t.cx, t.cy, t.r, deg);
						const [nx, ny] = polar(t.cx, t.cy, t.r - 16, deg);
						return (
							<g key={`station-${deg}`}>
								<circle className="a-node" cx={fmt(x)} cy={fmt(y)} r="3.2" />
								<text
									className="a-num"
									x={fmt(nx)}
									y={fmt(ny) + 3}
									textAnchor="middle"
								>
									{String(i + 1).padStart(2, "0")}
								</text>
							</g>
						);
					})}
					<circle className="a-node hollow" cx={t.cx} cy={t.cy} r="4.2" />
					<text className="a-note" x="862" y="474" textAnchor="end">
						approved context · v2
					</text>
					<Dim
						x1={490}
						y1={300}
						x2={490}
						y2={490}
						offset={-16}
						label="BUILT BY THE TWIN"
					/>
				</g>

				{/* ===== Callouts ===== */}
				<Leader
					target={[fmt(s1x), fmt(s1y)]}
					at={[930, 336]}
					main="Published definitions"
					dot={false}
				/>
				<Leader
					target={[fmt(s2x), fmt(s2y)]}
					at={[930, 402]}
					main="Versioned"
					dot={false}
				/>
				<Leader
					target={[fmt(s3x), fmt(s3y)]}
					at={[930, 468]}
					main="Evidence-linked"
					dot={false}
				/>

				{/* ===== Notes ===== */}
				<text className="a-note" x="48" y="556">
					01 · the model supplies intelligence
				</text>
				<text className="a-note" x="48" y="572">
					02 · the Twin supplies your approved business context
				</text>

				<TitleBlock
					x={544}
					y={524}
					width={600}
					drawing="MLX-DRW-001 · The missing context"
					rows={[
						["Drawn", "MLX"],
						["Proven", "Your database"],
						["Status", "Approved context", true],
						["Rev", "v2"],
					]}
				/>
			</Sheet>
		</Plate>
	);
}

/* ================================================================= */
/*  DRW-002 · Three states, exploded                                   */
/* ================================================================= */

const PLATE = { x0: 320, w: 400, d: 140, shear: 110, t: 8 };
const PLATE_Y = { approved: 110, derived: 300, source: 490 } as const;
const SKEW = PLATE.shear / PLATE.d;

/** Map plate-plane coords (u across, v into the page) to sheet coords. */
function onPlate(y0: number, u: number, v: number): [number, number] {
	return [fmt(PLATE.x0 + u - SKEW * v), fmt(y0 + v)];
}

function ExplodedPlate({ y0, children }: { y0: number; children?: ReactNode }) {
	const { x0, w, d, shear, t } = PLATE;
	const a = `${x0} ${y0}`;
	const b = `${x0 + w} ${y0}`;
	const c = `${x0 + w - shear} ${y0 + d}`;
	const dd = `${x0 - shear} ${y0 + d}`;
	return (
		<g>
			<path className="a-fill paper" d={`M${a} L${b} L${c} L${dd} Z`} />
			<path
				className="a-fill mid"
				d={`M${dd} L${c} L${x0 + w - shear} ${y0 + d + t} L${x0 - shear} ${y0 + d + t} Z`}
			/>
			<path
				className="a-fill mid"
				d={`M${b} L${c} L${x0 + w - shear} ${y0 + d + t} L${x0 + w} ${y0 + t} Z`}
			/>
			<path className="a-line strong" d={`M${a} L${b} L${c} L${dd} Z`} />
			<path
				className="a-line"
				d={`M${dd} V${y0 + d + t} H${x0 + w - shear} V${y0 + d} M${x0 + w - shear} ${y0 + d + t} L${x0 + w} ${y0 + t} V${y0}`}
			/>
			<g transform={`matrix(1 0 ${fmt(-SKEW)} 1 ${x0} ${y0})`}>{children}</g>
		</g>
	);
}

const SOURCE_COLS = 8;
const SOURCE_ROWS = 3;
const recU = (c: number) => 22 + c * 47;
const recV = (r: number) => 18 + r * 40;
/** Source records as plate-plane [u, v, col] — keyed on geometry, not index. */
const SOURCE_CELLS: Array<[number, number, number]> = Array.from(
	{ length: SOURCE_ROWS },
	(_, r) => r,
).flatMap((r) =>
	Array.from({ length: SOURCE_COLS }, (_, c): [number, number, number] => [
		recU(c),
		recV(r),
		c,
	]),
);
const DERIVED_NODES: Array<[number, number]> = [
	[60, 40],
	[150, 30],
	[250, 36],
	[330, 44],
	[110, 100],
	[210, 96],
	[300, 104],
];
const DERIVED_EDGES: Array<[number, number]> = [
	[0, 1],
	[1, 2],
	[2, 3],
	[0, 4],
	[1, 5],
	[4, 5],
	[5, 6],
	[2, 6],
];
/** Derived node → source record (col,row) it was read from. */
const DERIVED_SOURCES: Array<[number, number, number]> = [
	[0, 1, 0],
	[1, 2, 1],
	[2, 4, 0],
	[3, 6, 1],
	[4, 1, 2],
	[5, 4, 2],
	[6, 6, 2],
];
function derivedNode(i: number): [number, number] {
	return DERIVED_NODES[i] ?? [0, 0];
}

/** Approved node → derived node index. */
const APPROVED: Array<[number, number, number]> = [
	[60, 40, 0],
	[150, 30, 1],
	[110, 100, 4],
];

function Drw002() {
	const { x0, w, d, shear } = PLATE;
	const [lt1x, lt1y] = onPlate(PLATE_Y.approved, 150, 30);
	const [lt2x, lt2y] = onPlate(PLATE_Y.derived, 250, 36);
	const [lt3x, lt3y] = onPlate(PLATE_Y.source, recU(5) + 16, recV(0) + 12);
	return (
		<Plate
			figure="Fig. 02"
			caption="One business. Three states you can inspect."
		>
			<Sheet
				label="Exploded axonometric of three stacked plates: source evidence at the bottom, derived context in the middle, approved context on top recorded as approved v2, with dashed construction lines between layers and a dimension reading inspectable at every layer."
				height={760}
			>
				<SheetHeading
					label="Exploded view"
					sub="02 · Three states · first angle · scale 1:1"
				/>

				{/* corner construction lines between plates */}
				<path
					className="a-line hidden-line"
					d={`M${x0} ${PLATE_Y.approved} V${PLATE_Y.source} M${x0 + w} ${PLATE_Y.approved} V${PLATE_Y.source} M${x0 - shear} ${PLATE_Y.approved + d} V${PLATE_Y.source + d} M${x0 + w - shear} ${PLATE_Y.approved + d} V${PLATE_Y.source + d}`}
				/>

				{/* derived → source links, drawn under the plates */}
				{DERIVED_SOURCES.map(([n, c, r]) => {
					const [ux, uy] = onPlate(
						PLATE_Y.derived,
						derivedNode(n)[0],
						derivedNode(n)[1],
					);
					const [sx, sy] = onPlate(PLATE_Y.source, recU(c) + 16, recV(r) + 12);
					return (
						<path
							key={`link-${n}-${c}-${r}`}
							className="a-line hidden-line"
							d={`M${ux} ${uy} L${sx} ${sy}`}
						/>
					);
				})}
				{APPROVED.map(([u, v, n]) => {
					const [ax, ay] = onPlate(PLATE_Y.approved, u, v);
					const [dx, dy] = onPlate(
						PLATE_Y.derived,
						derivedNode(n)[0],
						derivedNode(n)[1],
					);
					return (
						<path
							key={`approve-${u}-${v}`}
							className="a-line hidden-line"
							d={`M${ax} ${ay} L${dx} ${dy}`}
						/>
					);
				})}

				{/* ===== 01 Source evidence ===== */}
				<ExplodedPlate y0={PLATE_Y.source}>
					{SOURCE_CELLS.map(([u, v, c]) => (
						<g key={`rec-${u}-${v}`}>
							<rect className="a-fill" x={u} y={v} width="32" height="24" />
							<rect className="a-line" x={u} y={v} width="32" height="24" />
							<path
								className="a-line faint"
								d={`M${u + 6} ${v + 8} H${u + 26} M${u + 6} ${v + 16} H${u + 18}`}
							/>
							{c < SOURCE_COLS - 1 ? (
								<path
									className="a-tick"
									d={`M${u + 32} ${v + 12} H${u + 47}`}
								/>
							) : null}
						</g>
					))}
				</ExplodedPlate>
				<text className="a-label" x="48" y={PLATE_Y.source + 34}>
					01 · Source evidence
				</text>
				<text className="a-sub" x="48" y={PLATE_Y.source + 49}>
					read-only · authoritative
				</text>

				{/* ===== 02 Derived ===== */}
				<ExplodedPlate y0={PLATE_Y.derived}>
					{DERIVED_EDGES.map(([i, j]) => (
						<line
							key={`edge-${i}-${j}`}
							className="a-line"
							x1={derivedNode(i)[0]}
							y1={derivedNode(i)[1]}
							x2={derivedNode(j)[0]}
							y2={derivedNode(j)[1]}
						/>
					))}
				</ExplodedPlate>
				{DERIVED_NODES.map(([u, v]) => {
					const [nx, ny] = onPlate(PLATE_Y.derived, u, v);
					return (
						<circle
							key={`dnode-${u}-${v}`}
							className="a-node hollow"
							cx={nx}
							cy={ny}
							r="4.6"
						/>
					);
				})}
				<text className="a-label" x="48" y={PLATE_Y.derived + 34}>
					02 · Derived
				</text>
				<text className="a-sub" x="48" y={PLATE_Y.derived + 49}>
					proposed · linked to sources
				</text>

				{/* ===== 03 Approved ===== */}
				<ExplodedPlate y0={PLATE_Y.approved}>
					<path className="a-line strong" d="M60 40 L150 30 L110 100 Z" />
				</ExplodedPlate>
				{APPROVED.map(([u, v]) => {
					const [nx, ny] = onPlate(PLATE_Y.approved, u, v);
					return (
						<circle
							key={`anode-${u}-${v}`}
							className="a-node"
							cx={nx}
							cy={ny}
							r="4.6"
						/>
					);
				})}
				<text className="a-label" x="48" y={PLATE_Y.approved + 34}>
					03 · Approved
				</text>
				<text className="a-sub" x="48" y={PLATE_Y.approved + 49}>
					durable · each version recorded
				</text>
				<Approval
					x={496}
					y={150}
					width={150}
					text="Approved · v2"
					sub="reviewed by your team"
				/>

				{/* ===== Callouts ===== */}
				<Leader
					target={[lt1x, lt1y]}
					at={[790, 96]}
					main="What your team approved"
					dot={false}
				/>
				<Leader
					target={[lt2x, lt2y]}
					at={[790, 346]}
					main="What it derived"
					dot={false}
				/>
				<Leader
					target={[lt3x, lt3y]}
					at={[790, 540]}
					main="What it read"
					dot={false}
				/>

				<Dim
					x1={960}
					y1={PLATE_Y.approved}
					x2={960}
					y2={PLATE_Y.source + d}
					offset={60}
					label="INSPECTABLE AT EVERY LAYER"
				/>

				{/* ===== Notes ===== */}
				<text className="a-note" x="48" y="690">
					01 · source evidence, transformation and the governed result remain
					distinct
				</text>
				<text className="a-note" x="48" y="706">
					02 · what your team approves becomes durable business context
				</text>

				<TitleBlock
					x={724}
					y={664}
					drawing="MLX-DRW-002 · Three states, exploded"
					rows={[
						["Drawn", "MLX"],
						["Proven", "Your database"],
						["Status", "Inspectable", true],
						["Layers", "3"],
					]}
				/>
			</Sheet>
		</Plate>
	);
}

/* ================================================================= */
/*  DRW-003 · The receipt                                              */
/* ================================================================= */

const RECEIPT = { x: 370, y: 64, w: 360, h: 436 };
const LEDGER_ROWS: Array<[string, string]> = [
	["Published definition", "Monthly revenue · v2"],
	["Data current to", "30 Jun 2026"],
	["Evidence used", "Published business view"],
	["If unsupported", "Says so · never guesses"],
];
const BARCODE = [
	3, 1, 2, 1, 3, 1, 1, 2, 3, 1, 2, 2, 1, 3, 1, 1, 2, 1, 3, 2, 1, 1, 2, 3, 1, 2,
	1, 1, 3, 1,
];

/** The receipt itself — shared by the full sheet and the mobile spot. */
function Drw003ReceiptBody() {
	const { x, y, w, h } = RECEIPT;
	const left = x + 20;
	const right = x + w - 20;
	const perf = Array.from(
		{ length: Math.floor(w / 12) },
		(_, i) => x + 6 + i * 12,
	);
	const ledgerTop = 246;
	const rowH = 30;
	return (
		<g>
					<rect className="a-fill paper" x={x} y={y} width={w} height={h} />
					<rect className="a-line strong" x={x} y={y} width={w} height={h} />
					{perf.map((px) => (
						<path
							key={`perf-${px}`}
							className="a-tick"
							d={`M${px} ${y - 5} V${y + 5} M${px} ${y + h - 5} V${y + h + 5}`}
						/>
					))}

					<text className="a-label" x={x + w / 2} y={102} textAnchor="middle">
						Governed answer
					</text>
					<text className="a-sub" x={x + w / 2} y={118} textAnchor="middle">
						issued by the Twin · as an agent receives it
					</text>
					<path className="a-line hidden-line" d={`M${left} 132 H${right}`} />

					{/* question */}
					<text className="a-sub" x={left} y={154}>
						Question
					</text>
					<text className="a-label" x={left} y={174}>
						What was revenue last quarter?
					</text>

					{/* answer — prints first, then the ledger rows follow */}
					<g className="m-print" style={printOrder(0)}>
						<text className="a-sub" x={left} y={204}>
							Answer
						</text>
						<text className="a-label accent" x={left} y={224}>
							£1,243,908 · Apr–Jun 2026
						</text>
					</g>
					<path
						className="a-line hidden-line"
						d={`M${left} ${ledgerTop - 6} H${right}`}
					/>

					{/* ledger */}
					{LEDGER_ROWS.map(([k, v], i) => {
						const ry = ledgerTop + i * rowH;
						return (
							<g key={k} className="m-print" style={printOrder(i + 1)}>
								<text className="a-sub" x={left} y={ry + 19}>
									{k}
								</text>
								<text
									className="a-note ink"
									x={right}
									y={ry + 19}
									textAnchor="end"
								>
									{v}
								</text>
								<path
									className="a-line faint"
									d={`M${left} ${ry + rowH} H${right}`}
								/>
							</g>
						);
					})}

					{/* evidence reference — barcode + seal */}
					<text className="a-sub" x={left} y={398}>
						Evidence reference
					</text>
					{BARCODE.map((bw, i) => {
						const bx = left + i * 7;
						return (
							<rect
								key={`bar-${bx}`}
								className="a-node"
								x={bx}
								y={406}
								width={bw}
								height={38}
							/>
						);
					})}
					<g>
						<circle className="a-ring" cx={right - 30} cy={425} r={22} />
						<circle className="a-ring faint" cx={right - 30} cy={425} r={12} />
						<TickRing
							cx={right - 30}
							cy={425}
							r0={22}
							r1={26}
							count={24}
							majorEvery={6}
							majorLen={4}
						/>
						<circle className="a-node hollow" cx={right - 30} cy={425} r="3" />
					</g>
					<path className="a-line hidden-line" d={`M${left} 458 H${right}`} />
					<text className="a-note" x={left} y={480}>
						illustrative specimen
					</text>
					<text className="a-sub" x={right} y={480} textAnchor="end">
						Mercury Labs · London
					</text>
		</g>
	);
}

/**
 * Portrait spot for small screens: the receipt alone, scaled to fill a
 * square spot canvas — no margin callouts, no sideways panning.
 */
export function Drw003Spot() {
	return (
		<Plate
			figure="Fig. 03"
			caption="Every governed answer carries its evidence."
			spot
		>
			<Sheet
				label="A till receipt drawn at large scale: the question, the governed answer, then ledger rows for the published definition, data currency, evidence used and the unsupported case."
				width={760}
				height={760}
			>
				<g transform="translate(100 38) scale(1.56) translate(-370 -64)">
					<Drw003ReceiptBody />
				</g>
			</Sheet>
		</Plate>
	);
}

function Drw003() {
	const { x, y, w, h } = RECEIPT;
	const left = x + 20;
	const right = x + w - 20;
	const ledgerTop = 246;
	const rowH = 30;
	return (
		<Plate
			figure="Fig. 03"
			caption="Every governed answer carries its evidence."
		>
			<Sheet
				label="A till receipt drawn at large scale: the question, the governed answer, then ledger rows for the published definition, data currency, evidence used and the unsupported case, each called out to the margin."
				height={620}
			>
				<SheetHeading label="Specimen" sub="03 · The receipt · scale 2:1" />
				<Drw003ReceiptBody />

				{/* ===== Callouts — left margin ===== */}
				<Leader
					target={[left - 6, 170]}
					at={[318, 150]}
					main="Asked as written"
					anchor="end"
					dot={false}
				/>
				<Leader
					target={[left - 6, ledgerTop + 15]}
					at={[318, 258]}
					main="Rule and version"
					anchor="end"
					dot={false}
				/>
				<Leader
					target={[left - 6, ledgerTop + 2 * rowH + 15]}
					at={[318, 346]}
					main="Where it came from"
					anchor="end"
					dot={false}
				/>

				{/* ===== Callouts — right margin ===== */}
				<Leader
					target={[right + 6, 220]}
					at={[772, 196]}
					main="Figure with its period"
					dot={false}
				/>
				<Leader
					target={[right + 6, ledgerTop + rowH + 15]}
					at={[772, 300]}
					main="Freshness stated"
					dot={false}
				/>
				<Leader
					target={[right + 6, ledgerTop + 3 * rowH + 15]}
					at={[772, 392]}
					main="No plausible guess"
					dot={false}
				/>

				<Dim
					x1={x + w}
					y1={y}
					x2={x + w}
					y2={y + h}
					offset={270}
					label="EVERY GOVERNED ANSWER"
				/>
				<Dim
					x1={x}
					y1={ledgerTop - 6}
					x2={x}
					y2={ledgerTop + 4 * rowH}
					offset={-262}
					label="EVIDENCE · ATTACHED"
				/>

				{/* ===== Notes ===== */}
				<text className="a-note" x="48" y="556">
					01 · figures shown are examples, not customer data
				</text>
				<text className="a-note" x="48" y="572">
					02 · an unsupported question is declared, never guessed
				</text>

				<TitleBlock
					x={724}
					y={524}
					drawing="MLX-DRW-003 · The receipt"
					rows={[
						["Issued by", "The Twin"],
						["Proven", "Your database"],
						["Status", "Governed answer", true],
					]}
				/>
			</Sheet>
		</Plate>
	);
}

/* ================================================================= */
/*  DRW-004 · The read-only valve                                      */
/* ================================================================= */

const WALL = 10;
const SRC = { x: 60, y: 150, w: 240, h: 280 };
const TWIN = { x: 860, y: 150, w: 220, h: 280 };
const VALVE = { x: 400, y: 210, w: 280, h: 160 };
const CHAMBER = { x: 430, y: 240, w: 220, h: 100 };
const BORE = { y: 270, h: 40 };
const PORTS: Array<[number, string]> = [
	[90, "Xero"],
	[180, "SunSystems"],
	[270, "Microsoft 365"],
];
const HINGE: [number, number] = [452, 242];
const FLAP_LEN = 80;
const FLAP_DEG = 38;

/** Hatched rectangular wall with its outline. */
function Wall({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
	return (
		<g>
			<Hatch x={x} y={y} width={w} height={h} />
			<rect className="a-line strong" x={x} y={y} width={w} height={h} />
		</g>
	);
}

/** Zig-zag compression spring between two x positions on a centreline. */
function springPath(
	x1: number,
	x2: number,
	cy: number,
	coils: number,
	amp: number,
) {
	const pitch = (x2 - x1) / (coils * 2);
	let d = `M${x1} ${cy}`;
	for (let i = 1; i <= coils * 2; i += 1) {
		const px = fmt(x1 + i * pitch);
		const py = i % 2 === 1 ? cy - amp : cy + amp;
		d += ` L${px} ${py}`;
	}
	return `${d} L${x2} ${cy}`;
}

function Drw004() {
	const [hx, hy] = HINGE;
	const tipX = fmt(hx + FLAP_LEN * Math.sin(FLAP_DEG * DEG));
	const tipY = fmt(hy + FLAP_LEN * Math.cos(FLAP_DEG * DEG));
	const midX = fmt(hx + 0.55 * FLAP_LEN * Math.sin(FLAP_DEG * DEG));
	const midY = fmt(hy + 0.55 * FLAP_LEN * Math.cos(FLAP_DEG * DEG));
	const ringC = { cx: TWIN.x + TWIN.w / 2, cy: 300, r: 56 };
	const portHalf = 12;
	// Top wall of the source vessel, split around the three port openings.
	const topSegments: Array<[number, number]> = [];
	let cursor = SRC.x;
	for (const [px] of PORTS) {
		topSegments.push([cursor, px - portHalf]);
		cursor = px + portHalf;
	}
	topSegments.push([cursor, SRC.x + SRC.w]);

	return (
		<Plate
			figure="Fig. 04"
			caption="Connections start read-only. Source systems remain authoritative."
		>
			<Sheet
				label="Section through a non-return valve: a hatched source-systems vessel with ports for Xero, SunSystems and Microsoft 365 feeds through a one-way flap valve into the Twin; flow runs left to right and a crossed-out return path is marked no write-back in a governed query."
				height={620}
			>
				<SheetHeading
					label="Section"
					sub="04 · The read-only valve · A–A · scale 1:1"
				/>

				{/* ===== Source systems vessel ===== */}
				<g>
					{topSegments.map(([a, b]) => (
						<Wall key={`top-${a}`} x={a} y={SRC.y} w={b - a} h={WALL} />
					))}
					<Wall x={SRC.x} y={SRC.y + SRC.h - WALL} w={SRC.w} h={WALL} />
					<Wall x={SRC.x} y={SRC.y} w={WALL} h={SRC.h} />
					<Wall
						x={SRC.x + SRC.w - WALL}
						y={SRC.y}
						w={WALL}
						h={BORE.y - SRC.y}
					/>
					<Wall
						x={SRC.x + SRC.w - WALL}
						y={BORE.y + BORE.h}
						w={WALL}
						h={SRC.y + SRC.h - BORE.y - BORE.h}
					/>
					{PORTS.map(([px, name]) => (
						<g key={`port-${px}`}>
							<path
								className="a-line strong"
								d={`M${px - portHalf} ${SRC.y} V112 M${px + portHalf} ${SRC.y} V112`}
							/>
							<path
								className="a-line"
								d={`M${px - portHalf - 6} 112 H${px + portHalf + 6}`}
							/>
							<text className="a-sub" x={px} y={100} textAnchor="middle">
								{name}
							</text>
							<Arrow d={`M${px} 122 V${SRC.y + 16}`} />
						</g>
					))}
					<text
						className="a-label"
						x={SRC.x + SRC.w / 2}
						y={204}
						textAnchor="middle"
					>
						Source systems
					</text>
					{PORTS.map(([px]) =>
						Array.from({ length: 4 }, (_, r) => {
							const ry = 228 + r * 40;
							return (
								<g key={`rec-${px}-${ry}`}>
									<rect
										className="a-fill"
										x={px - 24}
										y={ry}
										width="48"
										height="28"
									/>
									<rect
										className="a-line"
										x={px - 24}
										y={ry}
										width="48"
										height="28"
									/>
									<path
										className="a-line faint"
										d={`M${px - 16} ${ry + 10} H${px + 16} M${px - 16} ${ry + 19} H${px + 4}`}
									/>
								</g>
							);
						}),
					)}
					<text
						className="a-note"
						x={SRC.x + SRC.w / 2}
						y={452}
						textAnchor="middle"
					>
						remain authoritative
					</text>
				</g>

				{/* ===== Inlet pipe ===== */}
				<Wall
					x={SRC.x + SRC.w}
					y={BORE.y - WALL}
					w={VALVE.x - SRC.x - SRC.w}
					h={WALL}
				/>
				<Wall
					x={SRC.x + SRC.w}
					y={BORE.y + BORE.h}
					w={VALVE.x - SRC.x - SRC.w}
					h={WALL}
				/>

				{/* ===== Valve body in section ===== */}
				<g>
					<Wall x={VALVE.x} y={VALVE.y} w={VALVE.w} h={CHAMBER.y - VALVE.y} />
					<Wall
						x={VALVE.x}
						y={CHAMBER.y + CHAMBER.h}
						w={VALVE.w}
						h={VALVE.y + VALVE.h - CHAMBER.y - CHAMBER.h}
					/>
					<Wall
						x={VALVE.x}
						y={CHAMBER.y}
						w={CHAMBER.x - VALVE.x}
						h={BORE.y - CHAMBER.y}
					/>
					<Wall
						x={VALVE.x}
						y={BORE.y + BORE.h}
						w={CHAMBER.x - VALVE.x}
						h={CHAMBER.y + CHAMBER.h - BORE.y - BORE.h}
					/>
					<Wall
						x={CHAMBER.x + CHAMBER.w}
						y={CHAMBER.y}
						w={VALVE.x + VALVE.w - CHAMBER.x - CHAMBER.w}
						h={BORE.y - CHAMBER.y}
					/>
					<Wall
						x={CHAMBER.x + CHAMBER.w}
						y={BORE.y + BORE.h}
						w={VALVE.x + VALVE.w - CHAMBER.x - CHAMBER.w}
						h={CHAMBER.y + CHAMBER.h - BORE.y - BORE.h}
					/>
					{/* seat */}
					<rect
						className="a-fill mid"
						x={CHAMBER.x + 14}
						y={CHAMBER.y}
						width="6"
						height="12"
					/>
					<rect
						className="a-fill mid"
						x={CHAMBER.x + 14}
						y={CHAMBER.y + CHAMBER.h - 12}
						width="6"
						height="12"
					/>
					<path
						className="a-line"
						d={`M${CHAMBER.x + 14} ${CHAMBER.y} v12 h6 v-12 M${CHAMBER.x + 14} ${CHAMBER.y + CHAMBER.h} v-12 h6 v12`}
					/>
					{/* flap — closed position (hidden) and open position (solid) */}
					<path
						className="a-line hidden-line"
						d={`M${hx} ${hy} V${hy + FLAP_LEN}`}
					/>
					<path
						className="a-line hidden-line"
						d={`M${hx} ${hy + FLAP_LEN} A${FLAP_LEN} ${FLAP_LEN} 0 0 0 ${tipX} ${tipY}`}
					/>
					<path
						className="a-fill mid"
						d={`M${hx - 3} ${hy} L${tipX - 3} ${tipY} L${tipX + 3} ${tipY} L${hx + 3} ${hy} Z`}
					/>
					<path
						className="a-line strong"
						d={`M${hx - 3} ${hy} L${tipX - 3} ${tipY} L${tipX + 3} ${tipY} L${hx + 3} ${hy} Z`}
					/>
					<circle className="a-node hollow" cx={hx} cy={hy} r="4" />
					{/* return spring against a fixed stop */}
					<path className="a-line" d={springPath(midX + 4, 606, midY, 7, 9)} />
					<rect
						className="a-fill mid"
						x="606"
						y={midY - 22}
						width="6"
						height="44"
					/>
					<rect
						className="a-line strong"
						x="606"
						y={midY - 22}
						width="6"
						height="44"
					/>
					<path className="a-line" d={`M609 ${CHAMBER.y} V${midY - 22}`} />
					<text
						className="a-sub"
						x={VALVE.x + VALVE.w / 2}
						y={VALVE.y + VALVE.h + 20}
						textAnchor="middle"
					>
						non-return · opens one way
					</text>
				</g>

				{/* ===== Outlet pipe ===== */}
				<Wall
					x={VALVE.x + VALVE.w}
					y={BORE.y - WALL}
					w={TWIN.x - VALVE.x - VALVE.w}
					h={WALL}
				/>
				<Wall
					x={VALVE.x + VALVE.w}
					y={BORE.y + BORE.h}
					w={TWIN.x - VALVE.x - VALVE.w}
					h={WALL}
				/>

				{/* ===== The Twin vessel ===== */}
				<g>
					<Wall x={TWIN.x} y={TWIN.y} w={TWIN.w} h={WALL} />
					<Wall x={TWIN.x} y={TWIN.y + TWIN.h - WALL} w={TWIN.w} h={WALL} />
					<Wall x={TWIN.x + TWIN.w - WALL} y={TWIN.y} w={WALL} h={TWIN.h} />
					<Wall x={TWIN.x} y={TWIN.y} w={WALL} h={BORE.y - TWIN.y} />
					<Wall
						x={TWIN.x}
						y={BORE.y + BORE.h}
						w={WALL}
						h={TWIN.y + TWIN.h - BORE.y - BORE.h}
					/>
					<text className="a-label" x={ringC.cx} y={204} textAnchor="middle">
						The Twin
					</text>
					<path
						className="a-line axis"
						d={`M${ringC.cx - 76} ${ringC.cy} H${ringC.cx + 76} M${ringC.cx} ${ringC.cy - 76} V${ringC.cy + 76}`}
					/>
					<circle
						className="a-ring dash"
						cx={ringC.cx}
						cy={ringC.cy}
						r={ringC.r + 12}
					/>
					<circle className="a-ring" cx={ringC.cx} cy={ringC.cy} r={ringC.r} />
					<circle
						className="a-ring faint"
						cx={ringC.cx}
						cy={ringC.cy}
						r={ringC.r - 24}
					/>
					<TickRing
						cx={ringC.cx}
						cy={ringC.cy}
						r0={ringC.r}
						r1={ringC.r + 5}
						count={36}
						majorEvery={6}
						majorLen={5}
					/>
					{STATIONS.map((deg) => {
						const [sx, sy] = polar(ringC.cx, ringC.cy, ringC.r, deg);
						return (
							<circle
								key={`twin-station-${deg}`}
								className="a-node"
								cx={fmt(sx)}
								cy={fmt(sy)}
								r="3"
							/>
						);
					})}
					<circle className="a-node hollow" cx={ringC.cx} cy={ringC.cy} r="4" />
					<text className="a-note" x={ringC.cx} y={452} textAnchor="middle">
						governed business context
					</text>
				</g>

				{/* ===== Flow — the one live element ===== */}
				<Arrow
					d={`M${SRC.x + SRC.w - 30} ${BORE.y + 20} H${CHAMBER.x - 4} C${CHAMBER.x + 16} ${BORE.y + 20} ${CHAMBER.x + 24} ${CHAMBER.y + CHAMBER.h - 12} ${CHAMBER.x + 56} ${CHAMBER.y + CHAMBER.h - 12} H${CHAMBER.x + 150} C${CHAMBER.x + 190} ${CHAMBER.y + CHAMBER.h - 12} ${CHAMBER.x + 196} ${BORE.y + 20} ${CHAMBER.x + CHAMBER.w + 4} ${BORE.y + 20} H${TWIN.x + 36}`}
					accent
					animated
				/>

				{/* ===== Return path — crossed out ===== */}
				<Arrow
					d={`M${ringC.cx} ${TWIN.y + TWIN.h} V470 H${SRC.x + SRC.w - 24} V${SRC.y + SRC.h + 6}`}
					className="hidden-line"
				/>
				<path
					className="a-line strong"
					d="M548 452 L584 488 M584 452 L548 488"
				/>
				<circle className="a-ring" cx="566" cy="470" r="26" />
				<text className="a-note ink" x="566" y="516" textAnchor="middle">
					No write-back in a governed query
				</text>

				{/* ===== Dimensions ===== */}
				<Dim
					x1={VALVE.x}
					y1={VALVE.y}
					x2={VALVE.x + VALVE.w}
					y2={VALVE.y}
					offset={-110}
					label="SCOPED TO YOUR ORGANISATION"
				/>
				<Dim
					x1={SRC.x + SRC.w}
					y1={BORE.y + BORE.h + WALL}
					x2={TWIN.x}
					y2={BORE.y + BORE.h + WALL}
					offset={118}
					label="READ-ONLY · ONE DIRECTION"
				/>

				{/* ===== Callouts ===== */}
				<Leader
					target={[midX - 6, midY - 12]}
					at={[560, 132]}
					main="Hinged flap"
					dot={false}
				/>
				<Leader
					target={[560, midY - 9]}
					at={[700, 166]}
					main="Return spring"
					dot={false}
				/>

				{/* ===== Notes ===== */}
				<text className="a-note" x="48" y="556">
					01 · connections start read-only · scoped to your organisation
				</text>
				<text className="a-note" x="48" y="572">
					02 · normal governed queries carry no browser, terminal or write
					access
				</text>

				<TitleBlock
					x={724}
					y={524}
					drawing="MLX-DRW-004 · The read-only valve"
					rows={[
						["Scope", "Read-only", true],
						["Region", "EU or US"],
						["Status", "Available today"],
					]}
				/>
			</Sheet>
		</Plate>
	);
}

/* ================================================================= */
/*  Export                                                             */
/* ================================================================= */

export const DRAWINGS_A: ConceptSpec[] = [
	{
		id: "DRW-001",
		title: "The missing context",
		note: "Elevation: a sealed generic model, a dimension gap, and the business sheet the Twin fills in — from home section 01.",
		surface: "ground",
		width: "contained",
		render: () => <Drw001 />,
	},
	{
		id: "DRW-002",
		title: "Three states, exploded",
		note: "Exploded axonometric of source evidence, derived context and approved context — from home section 02, How the Twin forms.",
		surface: "ground",
		width: "contained",
		render: () => <Drw002 />,
	},
	{
		id: "DRW-003",
		title: "The receipt",
		note: "A till receipt at large scale: the governed answer with its definition, freshness, evidence and unsupported case — from the home specimen.",
		surface: "ground",
		width: "contained",
		render: () => <Drw003 />,
	},
	{
		id: "DRW-004",
		title: "The read-only valve",
		note: "Section through a non-return valve between source systems and the Twin — from the activation and buyer-questions copy on read-only connectors.",
		surface: "ground",
		width: "contained",
		render: () => <Drw004 />,
	},
];
